import { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  FormControl,
  Heading,
  VStack,
  Input,
  Container,
  Text,
  useToast,
  Progress,
  Badge,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Flex,
  Image,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiUpload,
  FiImage,
  FiCheckCircle,
  FiFolder
} from "react-icons/fi";
import * as Constants from "../../../constants/Constants";

const pageInputsConfig = {
  home: 11,
  about: 6,
  product: 11,
  project: 8,
  download: 3,
  contact: 1
};

const pageColors = {
  home: "blue",
  about: "purple",
  product: "green",
  project: "orange",
  download: "pink",
  contact: "teal"
};

const AdminUploadPage = () => {
  const pages = Object.keys(pageInputsConfig);
  const toast = useToast();

  const [images, setImages] = useState(
    pages.reduce((acc, page) => {
      acc[page] = Array(pageInputsConfig[page])
        .fill(null)
        .map(() => ({ key: "", file: null, preview: null }));
      return acc;
    }, {})
  );

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [allImages, setAllImages] = useState([]);

  // 🔐 Admin check
  useEffect(() => {
    const isAdmin = sessionStorage.getItem("isAdmin") === "true";
    if (!isAdmin) {
      window.location.href = "https://ensouniversal.com/";
    }
  }, []);

  useEffect(() => {
  axios.get(Constants.REACT_APP_API_URL + "api/upload")
    .then((res) => {
      if (res.data.success) {
        setAllImages(res.data.data);
      }
    })
    .catch((err) => console.log(err));
}, []);

  const handleChange = (page, index, field, value) => {
    const updated = [...images[page]];

    if (field === "file" && value) {
      // Create preview URL for the image
      const previewUrl = URL.createObjectURL(value);
      updated[index] = {
        ...updated[index],
        [field]: value,
        preview: previewUrl,
        key: value.name // Auto-fill key with filename
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }

    setImages({ ...images, [page]: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    let totalFiles = 0;

    pages.forEach((page) => {
      images[page].forEach((img, index) => {
        if (img.file) {
          const inputKey = `${page}_${index + 1}_image`;
          formData.append(inputKey, img.file);
          formData.append(`${inputKey}_name`, img.key || img.file.name);
          totalFiles++;
        }
      });
    });

    if (totalFiles === 0) {
      toast({
        title: "No files selected",
        description: "Please select at least one image to upload",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      setUploading(false);
      return;
    }

    try {
      await axios.post(Constants.REACT_APP_API_URL + "api/upload", formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      toast({
        title: "Upload successful",
        description: `${totalFiles} image(s) uploaded successfully`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      // Clear previews after successful upload
      pages.forEach((page) => {
        images[page].forEach((img, index) => {
          if (img.preview) {
            URL.revokeObjectURL(img.preview);
          }
        });
      });

    } catch (error) {
      console.error(error);
      toast({
        title: "Upload failed",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const getTotalImagesCount = () => {
    return Object.values(images).reduce((total, pageImages) => {
      return total + pageImages.filter(img => img.file).length;
    }, 0);
  };

  const totalSelected = getTotalImagesCount();
  const totalAllowed = Object.values(pageInputsConfig).reduce((a, b) => a + b, 0);

  const getPageUploadCount = (page) => {
    return images[page].filter(img => img.file).length;
  };
  console.log(allImages)
  return (
    <Box minH="100vh" bg="gray.50" py={8}>
      <Container maxW="1400px">
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Header Section */}
            <Flex
              justify="space-between"
              align="center"
              bg="white"
              p={6}
              borderRadius="lg"
              boxShadow="sm"
            >
              <Box>
                <Heading size="lg" color="gray.800" mb={2}>
                  🖼️ Image Upload Manager
                </Heading>
                <Text color="gray.600">
                  Manage and upload images for different sections of your website
                </Text>
              </Box>
              <Box colorScheme="green" p={3}>
                <Badge px={4} borderRadius="full" fontSize="sm" fontWeight="600" bg="green.50" color="green.600" border="1px solid" borderColor="green.200">{totalSelected} / {totalAllowed} Images Selected</Badge>
              </Box>
            </Flex>

            {/* Upload Progress */}
            {uploading && (
              <Box bg="white" p={4} borderRadius="lg" boxShadow="sm">
                <Flex justify="space-between" mb={2}>
                  <Text fontWeight="medium">Uploading images...</Text>
                  <Text>{uploadProgress}%</Text>
                </Flex>
                <Progress value={uploadProgress} size="lg" colorScheme="blue" borderRadius="full" />
              </Box>
            )}

            {/* Pages Grid */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {pages.map((page) => (
                <Card
                  key={page}
                  variant="outline"
                  bg="white"
                  boxShadow="md"
                  transition="all 0.3s"
                  _hover={{ boxShadow: "lg" }}
                >
                  <CardHeader
                    bg={`${pageColors[page]}.50`}
                    borderBottomWidth="1px"
                    py={4}
                  >
                    <Flex justify="space-between" align="center">
                      <Flex align="center" gap={2}>
                        <Icon as={FiFolder} color={`${pageColors[page]}.500`} boxSize={5} />
                        <Heading size="md" color={`${pageColors[page]}.700`} textTransform="capitalize">
                          {page}
                        </Heading>
                      </Flex>
                      <Badge colorScheme={pageColors[page]}>
                        {getPageUploadCount(page)} / {pageInputsConfig[page]} Uploaded
                      </Badge>
                    </Flex>
                  </CardHeader>

                  <CardBody>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {images[page].map((img, index) => (
                        <Box
                          key={index}
                          borderWidth="2px"
                          borderRadius="lg"
                          borderStyle={img.file ? "solid" : "dashed"}
                          borderColor={img.file ? `${pageColors[page]}.300` : "gray.200"}
                          p={4}
                          bg={img.file ? `${pageColors[page]}.50` : "white"}
                          transition="all 0.2s"
                          _hover={{ borderColor: `${pageColors[page]}.400` }}
                        >
                          <VStack spacing={3} align="stretch">
                            <Flex justify="space-between" align="center">
                              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                                Image {index + 1}
                              </Text>
                              {img.file && (
                                <Icon as={FiCheckCircle} color="green.500" boxSize={4} />
                              )}
                            </Flex>
                            {allImages
                              .filter((img) => img.fieldname === `${page}_${index + 1}_image`)
                              .map((img, i) => (
                                <Box key={i} borderWidth="1px" borderRadius="md" overflow="hidden">
                                  <Image
                                    src={img.image_url}
                                    alt={img.image_key}
                                    objectFit="cover"
                                    w="100%"
                                    h="80px"
                                  />
                                </Box>
                            ))}
                            {/* Image Preview */}
                            {img.preview ? (
                              <Box
                                borderRadius="md"
                                overflow="hidden"
                                bg="gray.100"
                                height="80px"
                                position="relative"
                              >
                                <Image
                                  src={img.preview}
                                  alt={`Preview ${index}`}
                                  objectFit="cover"
                                  w="100%"
                                  h="100%"
                                />
                              </Box>
                            ) : (
                              <Flex
                                height="80px"
                                bg="gray.50"
                                borderRadius="md"
                                align="center"
                                justify="center"
                                borderWidth="1px"
                                borderStyle="dashed"
                              >
                                <Icon as={FiImage} color="gray.400" boxSize={6} />
                              </Flex>
                            )}

                            {/* File Input */}
                            <FormControl>
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleChange(page, index, "file", e.target.files[0])}
                                size="sm"
                                p={1}
                                border="none"
                                _focus={{ boxShadow: "none" }}
                              />
                            </FormControl>

                            {/* Image Key Input */}
                            {img.file && (
                              <FormControl>
                                <Input
                                  placeholder="Image identifier (optional)"
                                  value={img.key}
                                  onChange={(e) => handleChange(page, index, "key", e.target.value)}
                                  size="sm"
                                  fontSize="xs"
                                  bg="white"
                                />
                              </FormControl>
                            )}
                          </VStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>

            {/* Submit Button */}
            <Flex justify="flex-end" mt={6}>
              <Tooltip label="Upload all selected images" hasArrow>
                <Button
                  type="submit"
                  size="lg"
                  colorScheme="blue"
                  leftIcon={<FiUpload />}
                  isLoading={uploading}
                  loadingText="Uploading..."
                  px={10}
                  py={6}
                  fontSize="md"
                  boxShadow="lg"
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "xl",
                  }}
                  transition="all 0.2s"
                >
                  Upload All Images ({getTotalImagesCount()})
                </Button>
              </Tooltip>
            </Flex>
          </VStack>
        </form>
      </Container>
    </Box>
  );
};

export default AdminUploadPage;